import { useRef, createContext, useContext, useMemo } from 'react'

export enum ScrollIntoViewId {
  MANIFEST = 'manifest',
  MAKE_AN_OFFER = 'makeAnOffer',
}

type ScrollIntoViewRefs = {
  [key in ScrollIntoViewId]: React.RefObject<HTMLDivElement | null> | null
}

const defaultContext: {
  scrollIntoView: (id: ScrollIntoViewId) => void
  refs: ScrollIntoViewRefs
} = {
  scrollIntoView: () => {
    console.warn(
      'scrollIntoView needs to be used within ScrollIntoViewProvider'
    )
  },
  refs: {
    [ScrollIntoViewId.MANIFEST]: null,
    [ScrollIntoViewId.MAKE_AN_OFFER]: null,
  },
}

const ScrollIntoViewContext = createContext<{
  scrollIntoView: (id: ScrollIntoViewId) => void
  refs: ScrollIntoViewRefs
}>(defaultContext)

const ScrollIntoViewProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const manifestRef = useRef<HTMLDivElement>(null)
  const makeAnOfferRef = useRef<HTMLDivElement>(null)

  const refs: ScrollIntoViewRefs = useMemo(
    () => ({
      [ScrollIntoViewId.MANIFEST]: manifestRef,
      [ScrollIntoViewId.MAKE_AN_OFFER]: makeAnOfferRef,
    }),
    [manifestRef, makeAnOfferRef]
  )

  const scrollIntoView = (id: ScrollIntoViewId) => {
    const ref = refs[id]
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <ScrollIntoViewContext.Provider value={{ scrollIntoView, refs }}>
      {children}
    </ScrollIntoViewContext.Provider>
  )
}

export const useScrollIntoView = () => useContext(ScrollIntoViewContext)

export const withScrollIntoView = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <ScrollIntoViewProvider>
      <Component {...props} />
    </ScrollIntoViewProvider>
  )

  return WrappedComponent
}
